(() => {
  const PRODUCT_FIELDS = [
    'productId',
    'name',
    'url',
    'imageUrl',
    'thumbUrl',
    'price',
    'listPrice',
    'brand',
    'customFields.key',
    'customFields.value',
  ];
  /** PLP / SERP sort radios in drawer + bar label (values map in #buildSort). */
  const SORT_OPTIONS = [
    { value: 'relevance', label: 'Most Relevant' },
    { value: 'date-descending', label: 'Latest' },
    { value: 'price-ascending', label: 'Price: Low to high' },
    { value: 'price-descending', label: 'Price: High to Low' },
  ];

  /** When `sort_by` is absent from the URL, Nosto uses this sort. */
  const DEFAULT_SORT = 'relevance';

  const FACET_DISPLAY_ORDER = [
    'material',
    'style',
    'price',
    'gemstone type',
    'gemstone colour',
    'gemstone color',
    'gemstone cut',
    'collection',
  ];

  /**
   * Nosto sometimes returns term values as JSON array strings, e.g. ["Diamond"] or ["A","B"].
   * Display only — URL/filter values stay raw.
   */
  const parseFacetDisplayValues = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return [];

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parsed.map((x) => String(x).trim()).filter(Boolean);
        }
      } catch {
        /* fall through */
      }

      const inner = s.slice(1, -1).trim();
      const quoted = inner.match(/"([^"]*)"/g);
      if (quoted?.length) {
        return quoted.map((p) => p.slice(1, -1).trim()).filter(Boolean);
      }

      if (inner.length) {
        return inner
          .split(',')
          .map((part) => part.trim().replace(/^["']|["']$/g, ''))
          .filter(Boolean);
      }

      const normalizedInner = inner.replace(/^["']|["']$/g, '').trim();
      return normalizedInner ? [normalizedInner] : [];
    }

    const normalized = s.replace(/^["']+|["']+$/g, '').trim();
    return normalized ? [normalized] : [];
  };

  const formatFacetDisplayValue = (raw) => {
    const values = parseFacetDisplayValues(raw);
    return values.join(', ');
  };

  /**
   * URL filter keys often use Nosto metafield paths (e.g. customFields.custom-gemstone_shape).
   * When facets are missing from the response (e.g. zero hits) or field ids differ, chips must not show raw keys.
   */
  const humanizeFacetFieldKey = (field) => {
    const raw = String(field ?? '').trim();
    if (!raw) return '';

    let s = raw
      .replace(/^skus\.customFields\./i, '')
      .replace(/^product\.customFields\./i, '')
      .replace(/^variants\.customFields\./i, '')
      .replace(/^customFields\./i, '');

    const leaf = (s.includes('.') ? s.split('.').pop() : s) || s;

    let core = leaf.replace(/^custom[-_]/i, '');
    core = core.replace(/[-_]+/g, ' ').trim();
    if (!core) core = leaf.replace(/[-_]+/g, ' ').trim();

    return core.replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  const inferMaterialSwatch = (raw) => {
    const s = formatFacetDisplayValue(raw).toLowerCase();
    if (s.includes('rose') && s.includes('gold')) return '#B76E79';
    if (s.includes('white') && s.includes('gold')) return '#E8E8E8';
    if (s.includes('yellow') && s.includes('gold')) return '#D4AF37';
    if (s.includes('silver') || s.includes('sterling')) return '#C0C0C0';
    if (s.includes('platinum')) return '#A8B0B8';
    if (s.includes('gold')) return '#D4AF37';
    return '';
  };

  const handleize = (value) => formatFacetDisplayValue(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const facetDisplayRank = (facet) => {
    const name = (facet?.name || '').toLowerCase();
    const field = (facet?.field || '').toLowerCase();
    let best = FACET_DISPLAY_ORDER.length + 2;
    FACET_DISPLAY_ORDER.forEach((key, i) => {
      if (name.includes(key) || field.includes(key.replace(/\s/g, ''))) {
        best = Math.min(best, i);
      }
    });
    return best;
  };

  const sortFacetsForDisplay = (facets) =>
    facets.slice().sort((a, b) => {
      const diff = facetDisplayRank(a) - facetDisplayRank(b);
      return diff !== 0 ? diff : (a.name || '').localeCompare(b.name || '');
    });

  const escapeHtml = (value) => {
    const element = document.createElement('span');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  };

  const escapeAttr = (value) => escapeHtml(value).replace(/"/g, '&quot;');

  const formatPrice = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    const locale = window.Shopify?.locale || document.documentElement.lang || 'en-GB';
    const currency = window.Shopify?.currency?.active || window.Shopify?.currency?.currency || 'GBP';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStorefrontRoot = () => {
    const rawRoot = String(window.Shopify?.routes?.root || '/').trim();
    if (!rawRoot || rawRoot === '/') return '/';

    return `/${rawRoot.replace(/^\/+|\/+$/g, '')}/`;
  };

  const hasLocalePrefix = (pathname) => /^\/[a-z]{2}(?:-[a-z]{2})?(?:\/|$)/i.test(pathname);

  const applyStorefrontRoot = (urlObject) => {
    if (!(urlObject instanceof URL)) return urlObject;
    if (urlObject.origin !== window.location.origin) return urlObject;

    const storefrontRoot = getStorefrontRoot();
    if (storefrontRoot === '/') return urlObject;
    if (urlObject.pathname.startsWith(storefrontRoot) || hasLocalePrefix(urlObject.pathname)) return urlObject;

    const rootedPath = `${storefrontRoot.replace(/\/$/, '')}${urlObject.pathname}`;
    return new URL(`${rootedPath}${urlObject.search}${urlObject.hash}`, window.location.origin);
  };

  const normalizeUrl = (url) => {
    if (!url) return '#';

    try {
      return applyStorefrontRoot(new URL(url, window.location.origin)).toString();
    } catch (error) {
      return '#';
    }
  };

  const extractHandle = (url) => {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const parts = pathname.split('/').filter(Boolean);
      const productIndex = parts.indexOf('products');
      return productIndex >= 0 ? parts[productIndex + 1] || '' : '';
    } catch (error) {
      return '';
    }
  };

  const formatTotal = (total) => (Number(total) >= 10000 ? '10,000+' : String(total ?? 0));

  const getFirstString = (...values) => {
    for (const value of values) {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) return trimmed;
        continue;
      }

      if (Array.isArray(value)) {
        const joined = value
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
          .join(', ');
        if (joined) return joined;
        continue;
      }

      if (value && typeof value === 'object') {
        const nested = getFirstString(value.value, value.label, value.text, value.name);
        if (nested) return nested;
      }
    }

    return '';
  };

  const getCustomFieldValue = (product, ...keys) => {
    const customFields = Array.isArray(product?.customFields) ? product.customFields : [];

    for (const key of keys) {
      const match = customFields.find((field) => String(field?.key || '').trim() === key);
      const value = getFirstString(match?.value);
      if (value) return value;
    }

    return '';
  };

  const getProductLabels = (product) =>
    [
      getCustomFieldValue(product, 'custom-product_label'),
      getCustomFieldValue(product, 'custom-product_label_2'),
    ].filter(Boolean);

  const waitForNostoJs = (timeout = 10000) =>
    new Promise((resolve, reject) => {
      const startedAt = Date.now();

      const poll = () => {
        if (typeof window.nostojs === 'function') {
          resolve(window.nostojs);
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          reject(new Error('Timed out waiting for Nosto storefront script.'));
          return;
        }

        window.setTimeout(poll, 50);
      };

      poll();
    });

  const getNostoSearchMethod = (api) => {
    if (typeof api?.search === 'function') {
      return api.search.bind(api);
    }

    if (typeof api?.search?.query === 'function') {
      return api.search.query.bind(api.search);
    }

    return null;
  };

  class NostoListingController {
    constructor(root) {
      this.root = root;
      this.type = root.dataset.nostoListingType;
      this.categoryId = root.dataset.nostoCategoryId || '';
      this.categoryPath = root.dataset.nostoCategoryPath || '';
      this.collectionHandle = root.dataset.nostoCollectionHandle || '';
      this.pageSize = Number(root.dataset.nostoPageSize || 24);
      this.pageMode = root.dataset.nostoPageMode || 'pagination';
      this.promoSectionId = root.dataset.nostoPromoSectionId || '';
      this.api = null;
      this.lastResponse = null;
      this.lastRequest = null;
      this.promos = this.#getPromos();
      this.promoMarkupByPosition = new Map();
      this.promoMarkupPromise = null;
      this.productCardMarkupCache = new Map();
      this.facetDisplayValueMaps = new Map();
      /** Markup currently written into the facets container, so identical responses skip the write. */
      this.renderedFacetsHtml = null;
      this.isLoadingMore = false;
      this.currentPage = this.#getCurrentPage();
      this.rangeCommitTimer = null;
      this.colourSwatches = this.#getColourSwatches();

      this.elements = {
        filtersShell: root.querySelector('[data-nosto-filters-shell]'),
        form: root.querySelector('[data-nosto-search-form]'),
        input: root.querySelector('[data-nosto-search-input]'),
        title: root.querySelector('[data-nosto-search-title]'),
        query: root.querySelector('[data-nosto-search-query]'),
        count: root.querySelector('[data-nosto-search-count]'),
        status: root.querySelector('[data-nosto-status]'),
        facets: root.querySelector('[data-nosto-facets]'),
        drawerApplied: root.querySelector('[data-nosto-drawer-applied]'),
        drawerAppliedWrap: root.querySelector('[data-nosto-drawer-applied-wrap]'),
        drawerFooter: root.querySelector('[data-nosto-drawer-footer]'),
        barClear: root.querySelector('[data-nosto-bar-clear]'),
        showResultsCount: root.querySelector('[data-nosto-show-results-count]'),
        sortDisplay: root.querySelector('[data-nosto-sort-display]'),
        products: root.querySelector('[data-nosto-products]'),
        pagination: root.querySelector('[data-nosto-pagination]'),
        emptyRecommendations: root.querySelector('[data-nosto-empty-recommendations]'),
      };
      this.emptyRecommendationsCarousel = null;

      this.onPopState = this.onPopState.bind(this);
      this.onRootClick = this.onRootClick.bind(this);
      this.onRootChange = this.onRootChange.bind(this);
      this.onRootInput = this.onRootInput.bind(this);
      this.onRootSubmit = this.onRootSubmit.bind(this);
    }

    async init() {
      this.#bindEvents();
      this.#renderSortOptions();

      if (this.type === 'serp' && !this.#getQuery()) {
        this.#renderSearchHeading({ total: 0, query: '' });
        this.#toggleFiltersVisibility(0, '');
        this.#toggleEmptyRecommendations(0, '');
        this.#setStatus('');
        return;
      }

      try {
        await this.#loadApi();
        await this.fetchAndRender();
      } catch (error) {
        console.warn('Nosto listing unavailable.', error);
        this.#setStatus('Search is unavailable at the moment, please try again.');
      }
    }

    async #loadApi() {
      if (this.api) return this.api;

      this.api = await new Promise((resolve, reject) => {
        waitForNostoJs()
          .then((nostojs) => {
            const timeoutId = window.setTimeout(() => {
              reject(new Error('Timed out waiting for Nosto JS API.'));
            }, 4000);

            nostojs((api) => {
              window.clearTimeout(timeoutId);
              resolve(api);
            });
          })
          .catch(reject);
      });

      return this.api;
    }

    #bindEvents() {
      window.addEventListener('popstate', this.onPopState);
      this.root.addEventListener('click', this.onRootClick);
      this.root.addEventListener('change', this.onRootChange);
      this.root.addEventListener('input', this.onRootInput);
      this.root.addEventListener('submit', this.onRootSubmit);
    }

    #getColourSwatches() {
      const script = this.root.querySelector('[data-nosto-colour-swatches]');
      if (!(script instanceof HTMLScriptElement)) return {};

      try {
        const parsed = JSON.parse(script.textContent || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        console.warn('Unable to parse Nosto colour swatches.', error);
        return {};
      }
    }

    #getMaterialSwatchData(raw) {
      const swatch = this.colourSwatches[handleize(raw)];
      if (swatch && typeof swatch === 'object') {
        return {
          colourCode: typeof swatch.colourCode === 'string' ? swatch.colourCode : '',
          colourImage: typeof swatch.colourImage === 'string' ? swatch.colourImage : '',
        };
      }

      return {
        colourCode: inferMaterialSwatch(raw),
        colourImage: '',
      };
    }

    #getQuery() {
      return new URL(window.location.href).searchParams.get('q')?.trim() || '';
    }

    #getCurrentPage() {
      const page = Number(new URL(window.location.href).searchParams.get('page') || 1);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    #getSortBy() {
      return new URL(window.location.href).searchParams.get('sort_by') || DEFAULT_SORT;
    }

    #getIsKeywordSearch() {
      return new URL(window.location.href).searchParams.get('nosto_keyword') === '1';
    }

    #getPromos() {
      const configElement = this.root.querySelector('[data-nosto-promos]');
      if (!(configElement instanceof HTMLScriptElement) || !configElement.textContent?.trim()) {
        return [];
      }

      try {
        const promos = JSON.parse(configElement.textContent);
        if (!Array.isArray(promos)) return [];

        return promos
          .map((promo) => ({
            position: Number(promo?.position || 0),
            colSpan: Number(promo?.colSpan || 1),
            rowSpan: Number(promo?.rowSpan || 1),
            hasShopTheImageProducts: Boolean(promo?.hasShopTheImageProducts),
            promoType: String(promo?.promoType || ''),
          }))
          .filter((promo) => Number.isFinite(promo.position) && promo.position > 0)
          .sort((a, b) => a.position - b.position);
      } catch (error) {
        console.warn('Unable to parse Nosto promo config.', error);
        return [];
      }
    }

    #getFilterState() {
      const params = new URL(window.location.href).searchParams;
      const termFilters = new Map();
      const displayFilters = new Map();
      const rangeFilters = new Map();

      for (const [key, value] of params.entries()) {
        if (key.startsWith('nf.')) {
          const field = key.slice(3);
          const existing = termFilters.get(field) || [];
          existing.push(value);
          termFilters.set(field, existing);
        }

        if (key.startsWith('nfd.')) {
          const field = key.slice(4);
          const existing = displayFilters.get(field) || [];
          existing.push(value);
          displayFilters.set(field, existing);
        }

        if (key.startsWith('nfr.')) {
          const rangeKey = key.slice(4);
          const lastDot = rangeKey.lastIndexOf('.');
          if (lastDot === -1) continue;

          const field = rangeKey.slice(0, lastDot);
          const bound = rangeKey.slice(lastDot + 1);
          const existing = rangeFilters.get(field) || {};
          existing[bound] = value;
          rangeFilters.set(field, existing);
        }
      }

      return { termFilters, displayFilters, rangeFilters };
    }

    #hasActiveFilters() {
      const { termFilters, displayFilters, rangeFilters } = this.#getFilterState();
      return termFilters.size > 0 || displayFilters.size > 0 || rangeFilters.size > 0;
    }

    #getSelectedMaterials() {
      const { termFilters, displayFilters } = this.#getFilterState();
      const materials = new Set();
      for (const field of new Set([...termFilters.keys(), ...displayFilters.keys()])) {
        if (!/^(?:(?:skus|variants)\.)?customFields\.material$/i.test(field)) continue;
        // Display selections are more precise than a raw term containing several values.
        const values = displayFilters.get(field)?.length
          ? displayFilters.get(field)
          : termFilters.get(field) || [];
        values.flatMap(parseFacetDisplayValues).forEach((value) => materials.add(value));
      }
      return [...materials];
    }

    /** Total applied constraints (each term value + each active range) for the mobile bar badge. */
    #getActiveFilterCount() {
      const { termFilters, displayFilters, rangeFilters } = this.#getFilterState();
      let n = 0;
      const fields = new Set([...termFilters.keys(), ...displayFilters.keys()]);
      for (const field of fields) {
        const displayValues = displayFilters.get(field) || [];
        const rawValues = termFilters.get(field) || [];
        n += displayValues.length || rawValues.length;
      }
      for (const range of rangeFilters.values()) {
        if (range.gte || range.gt || range.lte || range.lt) n += 1;
      }
      return n;
    }

    #resolveTermFilterValues(field, { rawValues = [], displayValues = [] } = {}) {
      const facetMap = this.facetDisplayValueMaps.get(field);
      if (!displayValues.length || !(facetMap instanceof Map) || !facetMap.size) {
        return rawValues;
      }

      const resolved = new Set();
      displayValues.forEach((displayValue) => {
        const option = facetMap.get(displayValue);
        if (option?.rawValues?.size) {
          option.rawValues.forEach((rawValue) => resolved.add(rawValue));
        } else if (displayValue) {
          resolved.add(displayValue);
        }
      });

      return resolved.size ? [...resolved] : rawValues;
    }

    #writeDisplayFacetSelection(url, field, displayValues) {
      const uniqueDisplayValues = [...new Set(displayValues.filter(Boolean))];
      const rawValues = this.#resolveTermFilterValues(field, {
        rawValues: [],
        displayValues: uniqueDisplayValues,
      });

      url.searchParams.delete(`nfd.${field}`);
      uniqueDisplayValues.forEach((value) => url.searchParams.append(`nfd.${field}`, value));

      url.searchParams.delete(`nf.${field}`);
      rawValues.forEach((value) => url.searchParams.append(`nf.${field}`, value));
    }

    #syncBarFilterBadge() {
      const count = this.#getActiveFilterCount();
      this.root.querySelectorAll('[data-nosto-bar-filter-badge]').forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (count > 0) {
          node.textContent = String(count);
          node.classList.remove('hidden');
          node.classList.add('inline-flex');
        } else {
          node.textContent = '';
          node.classList.add('hidden');
          node.classList.remove('inline-flex');
        }
      });
    }

    #shouldRenderPromos() {
      return this.type === 'category' && this.promos.length > 0 && !this.#hasActiveFilters();
    }

    async #loadPromoMarkup() {
      if (!this.#shouldRenderPromos() || !this.promoSectionId) {
        return this.promoMarkupByPosition;
      }

      if (this.promoMarkupByPosition.size > 0) {
        return this.promoMarkupByPosition;
      }

      if (this.promoMarkupPromise) {
        return this.promoMarkupPromise;
      }

      this.promoMarkupPromise = (async () => {
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('section_id', this.promoSectionId);

        const response = await fetch(requestUrl.toString());
        if (!response.ok) {
          throw new Error(`Failed to fetch promo markup: HTTP error ${response.status}`);
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const promoNodes = html.querySelectorAll('[data-plp-promo]');

        this.promoMarkupByPosition = new Map();
        promoNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          const position = Number(node.dataset.promoPosition || 0);
          if (!Number.isFinite(position) || position <= 0) return;

          const promoMarkup = node.innerHTML.trim();
          if (!promoMarkup) return;

          const existingPromos = this.promoMarkupByPosition.get(position) || [];
          existingPromos.push(promoMarkup);
          this.promoMarkupByPosition.set(position, existingPromos);
        });

        return this.promoMarkupByPosition;
      })();

      try {
        return await this.promoMarkupPromise;
      } finally {
        this.promoMarkupPromise = null;
      }
    }

    #buildFilters() {
      const { termFilters, displayFilters, rangeFilters } = this.#getFilterState();
      const filters = [];
      const fields = new Set([...termFilters.keys(), ...displayFilters.keys()]);

      for (const field of fields) {
        const values = this.#resolveTermFilterValues(field, {
          rawValues: termFilters.get(field) || [],
          displayValues: displayFilters.get(field) || [],
        });

        if (values.length) filters.push({ field, value: values });
      }

      for (const [field, range] of rangeFilters.entries()) {
        const normalizedRange = {};
        ['gt', 'gte', 'lt', 'lte'].forEach((key) => {
          if (range[key] !== undefined && range[key] !== '') {
            normalizedRange[key] = String(range[key]);
          }
        });

        if (Object.keys(normalizedRange).length) {
          filters.push({ field, range: normalizedRange });
        }
      }

      return filters;
    }

    #buildSort() {
      switch (this.#getSortBy()) {
        case 'date-descending':
          return [{ field: 'datePublished', order: 'desc' }];
        case 'price-ascending':
          return [{ field: 'price', order: 'asc' }];
        case 'price-descending':
          return [{ field: 'price', order: 'desc' }];
        default:
          return undefined;
      }
    }

    #buildRequest(page = this.currentPage, { size } = {}) {
      const products = {
        fields: PRODUCT_FIELDS,
        facets: ['*'],
        size: size ?? this.pageSize,
        from: (page - 1) * this.pageSize,
      };

      const sort = this.#buildSort();
      const filters = this.#buildFilters();

      if (sort) products.sort = sort;
      if (filters.length) products.filter = filters;

      if (this.type === 'category') {
        products.categoryId = this.categoryId;
        products.categoryPath = this.categoryPath;
        return { products };
      }

      return {
        query: this.#getQuery(),
        products,
      };
    }

    async fetchAndRender({ append = false } = {}) {
      const api = await this.#loadApi();
      const search = getNostoSearchMethod(api);
      const isRestoreLoad = !append && this.pageMode === 'load-more' && this.currentPage > 1;
      const request = isRestoreLoad
        ? this.#buildRequest(1, { size: this.currentPage * this.pageSize })
        : this.#buildRequest(this.currentPage);

      if (!search) {
        throw new Error('Nosto search API method was not found.');
      }

      this.lastRequest = request;
      this.#setStatus('Loading...');

      const response = await search(request, {
        track: this.type === 'category' ? 'category' : 'serp',
        redirect: true,
        isKeyword: this.#getIsKeywordSearch(),
      });

      this.lastResponse = response;
      await this.#render(response, { append, fromPage: isRestoreLoad ? 1 : this.currentPage });
      this.#setStatus('');
    }

    async #render(response, { append = false, fromPage = this.currentPage } = {}) {
      const products = response?.products?.hits || [];
      const total = Number(response?.products?.total || 0);
      const query = response?.query || this.#getQuery();
      const items = await this.#buildGridItems(products, total, { fromPage });

      this.#renderSearchHeading({ total, query });
      this.#renderFacets(response?.products?.facets || []);
      this.#renderActiveFilters();
      this.#renderProducts(items, { append });
      this.#renderPagination({ total, count: products.length });
      this.#renderSortOptions();
      this.#updateItemsCount(total);
      this.#updateShowResultsCount(total);
      this.#toggleFiltersVisibility(total, query);
      this.#toggleEmptyRecommendations(total, query);
    }

    #renderSearchHeading({ total, query }) {
      if (this.type !== 'serp') return;

      if (this.elements.title) {
        this.elements.title.textContent = total > 0 ? 'Search results for:' : query ? 'No results found for:' : 'Search';
      }

      if (this.elements.query) {
        this.elements.query.textContent = query ? `"${query}"` : '';
      }

      if (this.elements.count) {
        this.elements.count.textContent = query ? `${formatTotal(total)} results` : '';
      }

      if (this.elements.input instanceof HTMLInputElement && this.elements.input.value !== query) {
        this.elements.input.value = query;
      }
    }

    #sortLabelFor(value) {
      return SORT_OPTIONS.find((o) => o.value === value)?.label || SORT_OPTIONS.find((o) => o.value === DEFAULT_SORT)?.label;
    }

    #renderSortOptions() {
      const sortBy = this.#getSortBy();
      const sortLabel = this.#sortLabelFor(sortBy);

      if (this.elements.sortDisplay instanceof HTMLElement) {
        this.elements.sortDisplay.textContent = sortLabel;
      }

      const inline = this.elements.facets?.querySelector('[data-nosto-sort-inline-label]');
      if (inline) {
        inline.textContent = sortLabel;
      }

      this.root.querySelectorAll('[data-nosto-sort-radio]').forEach((radio) => {
        if (radio instanceof HTMLInputElement) {
          radio.checked = radio.value === this.#getSortBy();
        }
      });
    }

    #getFacetLabel(field) {
      const facets = this.lastResponse?.products?.facets || [];
      if (!field) return '';

      const exact = facets.find((f) => f.field === field);
      if (exact?.name) return exact.name;

      const fold = (s) => String(s || '').toLowerCase();
      const ci = facets.find((f) => fold(f.field) === fold(field));
      if (ci?.name) return ci.name;

      const leaf = field.includes('.') ? field.split('.').pop() : field;
      const byLeaf = facets.find((f) => {
        const fl = f.field?.includes('.') ? f.field.split('.').pop() : f.field;
        return fold(fl) === fold(leaf);
      });
      if (byLeaf?.name) return byLeaf.name;

      return humanizeFacetFieldKey(field);
    }

    #getFacetSelectionCount(field, facetType) {
      const { termFilters, displayFilters, rangeFilters } = this.#getFilterState();
      if (facetType === 'stats') {
        const r = rangeFilters.get(field);
        if (!r) return 0;
        return r.gte || r.gt || r.lte || r.lt ? 1 : 0;
      }
      return displayFilters.get(field)?.length || termFilters.get(field)?.length || 0;
    }

    #buildTermsFacetOptions(facet) {
      const optionMap = new Map();

      (facet.data || []).forEach((item) => {
        const values = parseFacetDisplayValues(item.value);
        const labels = values.length ? values : [formatFacetDisplayValue(item.value)].filter(Boolean);
        const count = Number(item.count);

        labels.forEach((label) => {
          const existing = optionMap.get(label) || {
            label,
            count: 0,
            rawValues: new Set(),
            swatchSource: item.value,
          };

          existing.count += Number.isFinite(count) ? count : 0;
          existing.rawValues.add(item.value);
          optionMap.set(label, existing);
        });
      });

      this.facetDisplayValueMaps.set(facet.field, optionMap);
      return [...optionMap.values()].sort((a, b) => a.label.localeCompare(b.label));
    }

    #renderSortAccordion() {
      const sortBy = this.#getSortBy();
      const sortLabel = this.#sortLabelFor(sortBy);
      const radioName = `nosto-sort-${this.type || 'listing'}`;

      const radios = SORT_OPTIONS.map(
        (option) => `
        <label class="nosto-plp-filters__choice flex cursor-pointer items-center gap-3 py-2 alt-body-small text-gray-700">
          <input
            type="radio"
            name="${escapeAttr(radioName)}"
            value="${escapeAttr(option.value)}"
            class="nosto-plp-filters__radio shrink-0 !p-0"
            data-nosto-sort-radio
            ${option.value === sortBy ? 'checked' : ''}
          >
          <span>${escapeHtml(option.label)}</span>
        </label>
      `
      ).join('');

      return `
        <details class="nosto-plp-filters__accordion border-b border-gray-400" data-nosto-sort-accordion>
          <summary class="nosto-plp-filters__summary flex cursor-pointer list-none items-center justify-between gap-3 py-4 pr-4 title-4 text-muted-black marker:content-none [&::-webkit-details-marker]:hidden">
            <span>Sort By</span>
            <span class="flex items-center gap-6 text-gray-600">
              <span class="alt-body-tiny text-black" data-nosto-sort-inline-label>${escapeHtml(sortLabel)}</span>
              <span class="nosto-plp-filters__caret !border-black" aria-hidden="true"></span>
            </span>
          </summary>
          <div class="pb-4 pt-1">
            ${radios}
          </div>
        </details>
      `;
    }

    #renderTermsFacet(facet) {
      const selectionCount = this.#getFacetSelectionCount(facet.field, 'terms');
      const badgeHtml = selectionCount
        ? `<span class="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-black px-1 text-[10px] font-medium leading-none text-white">${escapeHtml(String(selectionCount))}</span>`
        : '';

      const facetNameLower = (facet.name || '').toLowerCase();
      const showSwatch = facetNameLower.includes('material');
      const { termFilters, displayFilters } = this.#getFilterState();
      const activeDisplayValues = new Set(displayFilters.get(facet.field) || []);
      const activeRawValues = new Set(termFilters.get(facet.field) || []);

      const options = this.#buildTermsFacetOptions(facet)
        .map((option) => {
          const swatchHtml = showSwatch
            ? (() => {
                const swatch = this.#getMaterialSwatchData(option.swatchSource);

                if (swatch.colourImage) {
                  return `<span class="nosto-plp-filters__swatch shrink-0 overflow-hidden rounded-full border border-gray-200"><img src="${escapeAttr(swatch.colourImage)}" alt="" class="block h-full w-full object-cover" loading="lazy"></span>`;
                }

                const swatchStyle = swatch.colourCode ? `background-color:${swatch.colourCode}` : 'background-color:#E8E8E8';
                return `<span class="nosto-plp-filters__swatch shrink-0 rounded-full ml-2.5 border border-gray-200" style="${escapeAttr(swatchStyle)}"></span>`;
              })()
            : `<span class="mt-1 inline-block w-4 shrink-0" aria-hidden="true"></span>`;

          const rawValues = [...option.rawValues];
          const isSelected = activeDisplayValues.size
            ? activeDisplayValues.has(option.label)
            : rawValues.length === 1 && activeRawValues.has(rawValues[0]);

          return `
            <label class="nosto-plp-filters__choice flex cursor-pointer items-center gap-3 py-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                class="nosto-plp-filters__checkbox mt-1 shrink-0 !p-0"
                data-nosto-facet-input
                data-field="${escapeAttr(facet.field)}"
                data-display-value="${escapeAttr(option.label)}"
                data-raw-values="${escapeAttr(JSON.stringify(rawValues))}"
                value="${escapeAttr(option.label)}"
                ${isSelected ? 'checked' : ''}
              >
              <span class="flex flex-1 flex-wrap items-center gap-x-1 gap-y-0 alt-body-small text-gray-700">
                <span>${escapeHtml(option.label)}</span>
                <span class="font-normal text-gray-700">(${escapeHtml(option.count)})</span>
                ${swatchHtml}
              </span>
            </label>
          `;
        })
        .join('');

      return `
        <details class="nosto-plp-filters__accordion border-b border-gray-400" data-nosto-facet-field="${escapeAttr(facet.field)}">
          <summary class="nosto-plp-filters__summary flex cursor-pointer list-none items-center justify-between gap-3 py-4 pr-4 title-4 text-muted-black marker:content-none [&::-webkit-details-marker]:hidden">
            <span class="flex items-center">${escapeHtml(facet.name)}</span>
            <span class="flex gap-4 items-center">${badgeHtml}<span class="nosto-plp-filters__caret !border-black" aria-hidden="true"></span></span>
          </summary>
          <div class="nosto-plp-filters__facet-options max-h-72 overflow-y-auto overflow-x-hidden pb-4">
            ${options}
          </div>
        </details>
      `;
    }

    #renderStatsFacet(facet) {
      const field = facet.field;
      const rangeMin = 0;
      const rangeMax = Math.max(1, Math.ceil(Number(facet.max ?? 0)));
      const { rangeFilters } = this.#getFilterState();
      const selectedRange = rangeFilters.get(field) || {};

      let low = selectedRange.gte || selectedRange.gt ? Number(selectedRange.gte || selectedRange.gt) : rangeMin;
      let high = selectedRange.lte || selectedRange.lt ? Number(selectedRange.lte || selectedRange.lt) : rangeMax;
      if (!Number.isFinite(low)) low = rangeMin;
      if (!Number.isFinite(high)) high = rangeMax;
      low = Math.min(rangeMax, Math.max(rangeMin, low));
      high = Math.min(rangeMax, Math.max(rangeMin, high));
      if (low > high) [low, high] = [high, low];

      const minThumbFilled = low > rangeMin;
      const maxThumbFilled = high < rangeMax;
      const minThumbClass = `nosto-plp-range__thumb nosto-plp-range__thumb--min${minThumbFilled ? ' nosto-plp-range__thumb--filled' : ''}`;
      const maxThumbClass = `nosto-plp-range__thumb nosto-plp-range__thumb--max${maxThumbFilled ? ' nosto-plp-range__thumb--filled' : ''}`;

      const selectionCount = this.#getFacetSelectionCount(field, 'stats');
      const badgeHtml = selectionCount
        ? `<span class="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-black px-1 text-[10px] font-medium leading-none text-white">${escapeHtml(String(selectionCount))}</span>`
        : '';

      return `
        <details class="nosto-plp-filters__accordion border-b border-gray-400" data-nosto-facet-field="${escapeAttr(field)}">
          <summary class="nosto-plp-filters__summary flex cursor-pointer list-none items-center justify-between gap-3 py-4 pr-4 title-4 text-muted-black marker:content-none [&::-webkit-details-marker]:hidden">
            <span class="flex items-center">${escapeHtml(facet.name)}</span>
            <span class="flex gap-4 items-center">${badgeHtml}<span class="nosto-plp-filters__caret !border-black" aria-hidden="true"></span></span>
          </summary>
          <div class="pb-5 pt-4 px-4">
            <div
              class="nosto-plp-range nosto-plp-range-slider px-0.5"
              data-nosto-range-slider
              data-field="${escapeAttr(field)}"
              data-range-min="${escapeAttr(rangeMin)}"
              data-range-max="${escapeAttr(rangeMax)}"
            >
              <div class="nosto-plp-range__track relative mb-8 min-h-8">
                <div class="nosto-plp-range__rail pointer-events-none absolute left-0 right-0 top-1/2 z-0 h-[2px] -translate-y-1/2 rounded-full bg-gray-200"></div>
                <div class="nosto-plp-range__fill pointer-events-none absolute left-0 top-1/2 z-[1] h-[2px] -translate-y-1/2 rounded-full bg-black"></div>
                <input
                  type="range"
                  class="${minThumbClass}"
                  data-nosto-range-min
                  min="${escapeAttr(rangeMin)}"
                  max="${escapeAttr(rangeMax)}"
                  step="1"
                  value="${escapeAttr(low)}"
                  aria-label="${escapeHtml(facet.name)} minimum"
                >
                <input
                  type="range"
                  class="${maxThumbClass}"
                  data-nosto-range-max
                  min="${escapeAttr(rangeMin)}"
                  max="${escapeAttr(rangeMax)}"
                  step="1"
                  value="${escapeAttr(high)}"
                  aria-label="${escapeHtml(facet.name)} maximum"
                >
              </div>
              <div class="flex justify-between alt-body-small text-gray-700">
                <span data-nosto-range-label-min>${escapeHtml(formatPrice(low))}</span>
                <span data-nosto-range-label-max>${escapeHtml(formatPrice(high))}</span>
              </div>
            </div>
          </div>
        </details>
      `;
    }

    #primeRangeSliders() {
      const wraps = this.elements.facets?.querySelectorAll('[data-nosto-range-slider]');
      wraps?.forEach((wrap) => {
        if (wrap instanceof HTMLElement) this.#syncRangeControls(wrap);
      });
    }

    #syncRangeControls(wrap) {
      const rangeMin = Number(wrap.dataset.rangeMin);
      const rangeMax = Number(wrap.dataset.rangeMax);
      const minEl = wrap.querySelector('[data-nosto-range-min]');
      const maxEl = wrap.querySelector('[data-nosto-range-max]');
      if (!(minEl instanceof HTMLInputElement) || !(maxEl instanceof HTMLInputElement)) return;

      /* Full-span min/max keeps thumb positions aligned with .nosto-plp-range__fill (percent of facet range). */
      minEl.min = String(rangeMin);
      minEl.max = String(rangeMax);
      maxEl.min = String(rangeMin);
      maxEl.max = String(rangeMax);

      let low = Number(minEl.value);
      let high = Number(maxEl.value);
      if (low > high) {
        minEl.value = String(high);
        maxEl.value = String(low);
        low = Number(minEl.value);
        high = Number(maxEl.value);
      }

      this.#updateRangeFill(wrap);
      this.#updateRangeLabels(wrap, low, high);
      this.#updateRangeThumbStyles(wrap, low, high);
    }

    #updateRangeThumbStyles(wrap, low, high) {
      const rangeMin = Number(wrap.dataset.rangeMin);
      const rangeMax = Number(wrap.dataset.rangeMax);
      const minEl = wrap.querySelector('[data-nosto-range-min]');
      const maxEl = wrap.querySelector('[data-nosto-range-max]');
      if (!(minEl instanceof HTMLInputElement) || !(maxEl instanceof HTMLInputElement)) return;

      const eps = 0.01;
      minEl.classList.toggle('nosto-plp-range__thumb--filled', Number.isFinite(low) && low > rangeMin + eps);
      maxEl.classList.toggle('nosto-plp-range__thumb--filled', Number.isFinite(high) && high < rangeMax - eps);
    }

    #updateRangeFill(wrap) {
      const fill = wrap.querySelector('.nosto-plp-range__fill');
      const minEl = wrap.querySelector('[data-nosto-range-min]');
      const maxEl = wrap.querySelector('[data-nosto-range-max]');
      if (!(fill instanceof HTMLElement) || !(minEl instanceof HTMLInputElement) || !(maxEl instanceof HTMLInputElement)) {
        return;
      }

      const min = Number(wrap.dataset.rangeMin);
      const max = Number(wrap.dataset.rangeMax);
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return;

      const low = Number(minEl.value);
      const high = Number(maxEl.value);
      const p1 = ((low - min) / (max - min)) * 100;
      const p2 = ((high - min) / (max - min)) * 100;
      fill.style.left = `${p1}%`;
      fill.style.width = `${Math.max(0, p2 - p1)}%`;
    }

    #updateRangeLabels(wrap, low, high) {
      const lMin = wrap.querySelector('[data-nosto-range-label-min]');
      const lMax = wrap.querySelector('[data-nosto-range-label-max]');
      if (lMin) lMin.textContent = formatPrice(low);
      if (lMax) lMax.textContent = formatPrice(high);
    }

    async #commitRangeFilter(wrap, low, high, rangeMin, rangeMax) {
      const field = wrap.dataset.field;
      if (!field) return;

      const eps = 0.01;
      this.#updateUrl((url) => {
        url.searchParams.delete(`nfr.${field}.gt`);
        url.searchParams.delete(`nfr.${field}.gte`);
        url.searchParams.delete(`nfr.${field}.lt`);
        url.searchParams.delete(`nfr.${field}.lte`);

        if (low > rangeMin + eps) url.searchParams.set(`nfr.${field}.gte`, String(low));
        if (high < rangeMax - eps) url.searchParams.set(`nfr.${field}.lte`, String(high));
        url.searchParams.delete('page');
      });

      await this.fetchAndRender();
    }

    #normalizeFacetShortcutLabel(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/colour/g, 'color')
        .replace(/\s+/g, ' ');
    }

    #syncShortcutFacetFields(facets) {
      this.root.querySelectorAll('[data-nosto-filter-shortcut]').forEach((btn) => {
        const label = this.#normalizeFacetShortcutLabel(btn.dataset.nostoFilterShortcut);
        const facet = facets.find((f) => this.#normalizeFacetShortcutLabel(f.name) === label);
        if (facet?.field) {
          btn.dataset.nostoFacetField = facet.field;
        } else {
          btn.removeAttribute('data-nosto-facet-field');
        }
      });
    }

    #syncShortcutBadges() {
      const { termFilters, displayFilters } = this.#getFilterState();
      this.root.querySelectorAll('[data-nosto-filter-shortcut]').forEach((btn) => {
        const field = btn.dataset.nostoFacetField;
        const badge = btn.querySelector('[data-nosto-shortcut-badge]');
        if (!(badge instanceof HTMLElement)) return;

        const n = field ? displayFilters.get(field)?.length || termFilters.get(field)?.length || 0 : 0;
        if (n > 0) {
          badge.textContent = String(n);
          badge.classList.remove('hidden');
          badge.classList.add('inline-flex');
        } else {
          badge.textContent = '';
          badge.classList.add('hidden');
          badge.classList.remove('inline-flex');
        }
      });
    }

    #updateItemsCount(total) {
      const nodes = [...this.root.querySelectorAll('[data-nosto-items-count]')];
      if (!nodes.length) return;

      const ph = nodes[0].dataset.itemsPlaceholder || '__NOSTO_COUNT__';
      const oneTpl = nodes[0].dataset.itemsOne || '';
      const otherTpl = nodes[0].dataset.itemsOther || '';
      const n = Number(total) || 0;
      const tpl = n === 1 ? oneTpl : otherTpl;
      const text = tpl.split(ph).join(formatTotal(n));
      nodes.forEach((el) => {
        el.textContent = text;
      });
    }

    #updateShowResultsCount(total) {
      if (this.elements.showResultsCount instanceof HTMLElement) {
        this.elements.showResultsCount.textContent = formatTotal(total);
      }
    }

    #toggleFiltersVisibility(total, query = this.#getQuery()) {
      if (!(this.elements.filtersShell instanceof HTMLElement)) return;
      const hasResults = Number(total) > 0;

      if (this.type === 'serp') {
        const hasQuery = Boolean(String(query || '').trim());
        this.elements.filtersShell.classList.toggle('hidden', !(hasQuery && hasResults));
        return;
      }

      if (this.type === 'category') {
        this.elements.filtersShell.classList.toggle('hidden', !hasResults);
      }
    }

    #toggleEmptyRecommendations(total, query = this.#getQuery()) {
      const emptyRecommendations = this.elements.emptyRecommendations;
      if (!(emptyRecommendations instanceof HTMLElement)) return;

      const shouldShow = this.type === 'serp' && Boolean(String(query || '').trim()) && Number(total) === 0;
      emptyRecommendations.hidden = !shouldShow;
      emptyRecommendations.classList.toggle('hidden', !shouldShow);

      if (!shouldShow) return;

      this.#initEmptyRecommendationsCarousel();
    }

    #initEmptyRecommendationsCarousel() {
      if (this.emptyRecommendationsCarousel) return;

      const emptyRecommendations = this.elements.emptyRecommendations;
      if (!(emptyRecommendations instanceof HTMLElement)) return;

      const carousel = emptyRecommendations.querySelector('[data-nosto-empty-recommendations-carousel]');
      if (!(carousel instanceof HTMLElement)) return;

      if (typeof window.Splide !== 'function') {
        window.setTimeout(() => this.#initEmptyRecommendationsCarousel(), 50);
        return;
      }

      this.emptyRecommendationsCarousel = new window.Splide(carousel, {
        autoWidth: true,
        perMove: 1,
        easingFunc: true,
        pagination: false,
        type: 'slide',
        drag: true,
        arrows: true,
        gap: 20,
      });

      this.emptyRecommendationsCarousel.mount();
    }

    #toggleBarClearVisibility() {
      const active = this.#hasActiveFilters();
      if (this.elements.barClear instanceof HTMLElement) {
        this.elements.barClear.classList.toggle('hidden', !active);
      }
    }

    #renderFilterShortcuts(sortedFacets) {
      const nav = this.root.querySelector('[data-nosto-filter-shortcuts]');
      const divider = this.root.querySelector('[data-nosto-filter-shortcuts-divider]');
      if (!(nav instanceof HTMLElement)) return;

      const eligible = sortedFacets.filter(
        (f) => f && (f.type === 'terms' || f.type === 'stats') && String(f.name || '').trim()
      );
      const shortcuts = eligible.slice(0, 4);

      if (!shortcuts.length) {
        nav.innerHTML = '';
        if (divider instanceof HTMLElement) {
          divider.classList.add('hidden');
          divider.classList.remove('inline-block');
        }
        return;
      }

      const btnClass =
        'nosto-plp-filters__shortcut inline-flex items-center gap-1.5 border-0 bg-transparent p-0 alt-body-tiny text-muted-black cursor-pointer anim-link pb-0.5';

      nav.innerHTML = shortcuts
        .map((facet) => {
          const name = String(facet.name || '').trim();
          return `
            <button type="button" class="${btnClass}" data-nosto-filter-shortcut="${escapeAttr(name)}">
              <span>${escapeHtml(name)}</span>
              <span class="hidden min-h-5 min-w-5 items-center justify-center rounded-full bg-black body-tiny text-white" data-nosto-shortcut-badge aria-hidden="true"></span>
            </button>
          `.trim();
        })
        .join('');

      if (divider instanceof HTMLElement) {
        divider.classList.remove('hidden');
        divider.classList.add('inline-block');
      }
    }

    #renderFacets(facets) {
      if (!(this.elements.facets instanceof HTMLElement)) return;

      const sorted = sortFacetsForDisplay(facets);
      this.facetDisplayValueMaps = new Map();
      this.#renderFilterShortcuts(sorted);
      const parts = [this.#renderSortAccordion()];

      // Note that `#renderTermsFacet` also populates `facetDisplayValueMaps`, so the markup is
      // always generated even when it turns out not to need writing.
      sorted.forEach((facet) => {
        if (facet.type === 'terms') parts.push(this.#renderTermsFacet(facet));
        else if (facet.type === 'stats') parts.push(this.#renderStatsFacet(facet));
      });

      const html = parts.join('');

      // Sorting, paginating and load-more leave the facet list untouched, so the markup is
      // byte-identical to what is already rendered. Writing it anyway would rebuild every
      // accordion — a cost that is invisible at the time, because a closed <dialog> is
      // `display: none` and its contents are never styled, and then falls due in one lump
      // inside the next `showModal()`.
      if (html !== this.renderedFacetsHtml) {
        const openSections = this.#getOpenFacetSections();

        this.elements.facets.innerHTML = html;
        this.renderedFacetsHtml = html;

        this.#restoreOpenFacetSections(openSections);
        this.#primeRangeSliders();
      }

      this.#syncShortcutFacetFields(sorted);
      this.#syncShortcutBadges();
    }

    /**
     * Identifies an accordion so its open state can survive a rebuild.
     *
     * @param {Element} details - A `<details>` inside the facets container.
     * @returns {string} A stable key, or an empty string if the element is not an accordion.
     */
    #facetSectionKey(details) {
      if (!(details instanceof HTMLElement)) return '';
      if (details.hasAttribute('data-nosto-sort-accordion')) return 'sort';

      return details.dataset.nostoFacetField || '';
    }

    /** @returns {Set<string>} Keys of the accordions currently expanded. */
    #getOpenFacetSections() {
      const open = new Set();
      if (!(this.elements.facets instanceof HTMLElement)) return open;

      this.elements.facets.querySelectorAll('details[open]').forEach((details) => {
        const key = this.#facetSectionKey(details);
        if (key) open.add(key);
      });

      return open;
    }

    /**
     * Re-expands the accordions that were open before a rebuild.
     *
     * Without this, checking a filter box collapses the very accordion the shopper is using,
     * because the whole facets container is replaced on every response.
     *
     * @param {Set<string>} openSections - Keys returned by `#getOpenFacetSections`.
     */
    #restoreOpenFacetSections(openSections) {
      if (!openSections.size || !(this.elements.facets instanceof HTMLElement)) return;

      this.elements.facets.querySelectorAll('details').forEach((details) => {
        const key = this.#facetSectionKey(details);
        if (key && openSections.has(key)) details.open = true;
      });
    }

    #renderActiveFilters() {
      const { termFilters, displayFilters, rangeFilters } = this.#getFilterState();
      const pills = [];
      const fields = new Set([...termFilters.keys(), ...displayFilters.keys()]);

      for (const field of fields) {
        const facetLabel = this.#getFacetLabel(field);
        const displayValues = displayFilters.get(field) || [];
        const values = displayValues.length ? displayValues : termFilters.get(field) || [];

        values.forEach((value) => {
          const displayVal = displayValues.length ? value : formatFacetDisplayValue(value);
          const labelText = `${facetLabel}: ${displayVal}`;
          pills.push(`
            <button
              type="button"
              class="nosto-plp-filters__chip"
              data-nosto-remove-filter
              data-field="${escapeAttr(field)}"
              ${displayValues.length ? `data-display-value="${escapeAttr(value)}"` : `data-value="${escapeAttr(value)}"`}
              aria-label="${escapeAttr(`${labelText}, remove`)}"
            >
              <span class="nosto-plp-filters__chip-text alt-body-small">${escapeHtml(labelText)}</span>
              <span class="nosto-plp-filters__chip-dismiss" aria-hidden="true">×</span>
            </button>
          `);
        });
      }

      for (const [field, range] of rangeFilters.entries()) {
        const min = range.gte || range.gt || '';
        const max = range.lte || range.lt || '';
        if (!min && !max) continue;

        const facetLabel = this.#getFacetLabel(field);
        const minN = Number(min);
        const maxN = Number(max);
        const labelText = `${facetLabel}: ${formatPrice(minN)} – ${formatPrice(maxN)}`;

        pills.push(`
          <button
            type="button"
            class="nosto-plp-filters__chip"
            data-nosto-clear-range
            data-field="${escapeAttr(field)}"
            aria-label="${escapeAttr(`${labelText}, remove`)}"
          >
            <span class="nosto-plp-filters__chip-text alt-body-small">${escapeHtml(labelText)}</span>
            <span class="nosto-plp-filters__chip-dismiss" aria-hidden="true">×</span>
          </button>
        `);
      }

      const hasPills = pills.length > 0;
      const html = pills.join('');

      if (this.elements.drawerApplied instanceof HTMLElement) {
        this.elements.drawerApplied.innerHTML = html;
      }

      if (this.elements.drawerAppliedWrap instanceof HTMLElement) {
        this.elements.drawerAppliedWrap.classList.toggle('hidden', !hasPills);
      }

      if (this.elements.drawerFooter instanceof HTMLElement) {
        this.elements.drawerFooter.classList.toggle('hidden', !hasPills);
        this.elements.drawerFooter.classList.toggle('flex', hasPills);
      }

      this.#toggleBarClearVisibility();
      this.#syncShortcutBadges();
      this.#syncBarFilterBadge();
    }

    async #buildGridItems(products, total, { fromPage = this.currentPage } = {}) {
      const items = [];

      if (!this.#shouldRenderPromos()) {
        return products.map((product) => ({ type: 'product', product }));
      }

      let promoMarkupByPosition;
      try {
        promoMarkupByPosition = await this.#loadPromoMarkup();
      } catch (error) {
        console.warn('Unable to load collection promo markup.', error);
        return products.map((product) => ({ type: 'product', product }));
      }

      if (!promoMarkupByPosition.size) {
        return products.map((product) => ({ type: 'product', product }));
      }

      const previousProductCount = this.pageSize * fromPage - this.pageSize;
      let previousPageCount = previousProductCount;
      let previousPromoCount = 0;

      this.promos.forEach((promo) => {
        if (promo.position <= previousPageCount) {
          previousPromoCount += 1;
        }
      });

      previousPageCount += previousPromoCount;

      let displayIndex = 0;
      const totalPages = Math.max(1, Math.ceil(total / this.pageSize));

      const appendPromosAtPosition = (position) => {
        const promoMarkup = promoMarkupByPosition.get(position) || [];
        if (!promoMarkup.length) return false;

        promoMarkup.forEach((markup) => {
          items.push({ type: 'promo', markup });
        });

        return true;
      };

      products.forEach((product) => {
        displayIndex += 1;
        let currentIndex = displayIndex + previousPageCount;

        while (appendPromosAtPosition(currentIndex)) {
          displayIndex += 1;
          currentIndex = displayIndex + previousPageCount;
        }

        items.push({ type: 'product', product });
      });

      if (products.length > 0 && this.currentPage === totalPages) {
        let canAppendTrailingPromo = true;

        while (canAppendTrailingPromo) {
          displayIndex += 1;
          const currentIndex = displayIndex + previousPageCount;
          canAppendTrailingPromo = appendPromosAtPosition(currentIndex);
        }
      }

      return items;
    }

    #renderProducts(items, { append = false } = {}) {
      if (!(this.elements.products instanceof HTMLElement)) return;

      if (!items.length && !append) {
        this.elements.products.innerHTML = `
          <div class="col-span-full my-8 text-center text-base">
            No products found.
          </div>
        `;
        return;
      }

      const markup = items
        .map((item) => {
          if (item.type === 'promo') return item.markup;
          return this.#renderProduct(item.product);
        })
        .join('');

      const startIndex = append ? this.elements.products.children.length : 0;

      if (append) {
        this.elements.products.insertAdjacentHTML('beforeend', markup);
      } else {
        this.elements.products.innerHTML = markup;
      }

      this.#initAlpineForInsertedNodes(startIndex);
      this.#hydrateProductCards();
    }

    /**
     * Initialises Alpine on the nodes just written into the grid.
     *
     * Scoped rather than a single `initTree` over the whole grid, for two reasons. An
     * unhydrated placeholder carries no `x-` directives, so walking it finds nothing — its
     * hydrate pass calls `initTree` itself once the real card markup is in place. And on a
     * `load-more` append the grid already holds initialised cards, which a grid-wide walk
     * would re-initialise, resetting any swatch the shopper had selected.
     *
     * @param {number} startIndex - Index of the first newly inserted child.
     */
    #initAlpineForInsertedNodes(startIndex) {
      if (!(this.elements.products instanceof HTMLElement)) return;
      if (!window.Alpine || typeof Alpine.initTree !== 'function') return;

      Array.from(this.elements.products.children)
        .slice(startIndex)
        .forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          const shell = node.querySelector('[data-nosto-card-shell]');
          if (shell instanceof HTMLElement && shell.dataset.nostoCardHydrated !== 'true') return;

          Alpine.initTree(node);
        });
    }

    /**
     * Cache key for a rendered `section_id=product-card` response.
     *
     * @param {string} handle - The product handle.
     * @param {string} url - The product URL, used when no handle could be parsed.
     * @returns {string} The cache key.
     */
    #cardCacheKey(handle, url) {
      return `${this.collectionHandle || 'default'}::${handle || url}`;
    }

    #renderProduct(product) {
      const url = normalizeUrl(product.url);
      const handle = extractHandle(url);
      const cachedMarkup = this.productCardMarkupCache.get(this.#cardCacheKey(handle, url));

      // A card whose section markup is already cached is rendered in its final form here, so
      // `#hydrateProductCards` skips it entirely. Without this every filter, sort and page
      // change builds a throwaway placeholder for all 24 cards and then immediately replaces
      // it — two full subtree style recalcs where one will do.
      const shellContents = cachedMarkup ?? this.#renderProductPlaceholder(product, url);

      return `
        <div class="product-grid__item" data-nosto-hit data-product-id="${escapeAttr(product.productId)}" data-product-url="${escapeAttr(
          url
        )}" data-product-handle="${escapeAttr(handle)}">
          <div data-nosto-card-shell data-nosto-materials="${escapeAttr(JSON.stringify(this.#getSelectedMaterials()))}" data-nosto-search-query="${escapeAttr(this.type === 'serp' ? this.#getQuery() : '')}" data-nosto-card-url="${escapeAttr(url)}" data-nosto-card-handle="${escapeAttr(handle)}"${
            cachedMarkup ? ' data-nosto-card-hydrated="true"' : ''
          }>
            ${shellContents}
          </div>
        </div>
      `;
    }

    /**
     * Markup shown while a card waits for its `section_id=product-card` response.
     *
     * Deliberately inert: an empty media box that holds the card's height, plus title and
     * price. Two things it no longer does.
     *
     * It used to carry a working `<quick-add-component>`, its `<quick-add-dialog>` and a
     * Klaviyo favourites slot, all of which were destroyed a few milliseconds later when the
     * real card replaced them — 48 custom elements and 24 `<dialog>` elements constructed,
     * connected, ref-scanned and torn down per grid render, for no visible benefit. Tapping
     * that quick add button during the hydration window also left an open dialog attached to a
     * detached subtree.
     *
     * And it used to render an `<img>` from Nosto's `imageUrl`. That is a different URL from
     * the one `snippets/product-card.liquid` serves — the real card uses a responsive
     * `srcset` at 400/600/900 with `sizes`, whereas `imageUrl` is a single unsized variant —
     * so every card downloaded its image twice, the placeholder's being the heavier of the
     * two. The media box is kept (`aspect-product-image`) so removing the image costs no
     * layout shift; the card is simply blank until its own image arrives.
     *
     * @param {object} product - A Nosto search hit.
     * @param {string} url - The already normalised product URL.
     * @returns {string} Placeholder markup for the card shell.
     */
    #renderProductPlaceholder(product, url) {
      const name = product.name || '';
      const price = formatPrice(product.price);
      const listPrice = formatPrice(product.listPrice);
      const productLabels = getProductLabels(product);
      const showListPrice =
        Number.isFinite(Number(product.listPrice)) &&
        Number.isFinite(Number(product.price)) &&
        Number(product.listPrice) > Number(product.price);

      return `
            <div class="group relative flex h-full flex-col">
            <div class="relative mb-2 overflow-hidden bg-primary-stone aspect-product-image lg:mb-3">
              <a href="${escapeAttr(url)}" class="block h-full">
                ${
                  productLabels.length
                    ? `
                      <div class="absolute top-3 left-3 z-10 flex flex-col items-start gap-1">
                        ${productLabels
                          .map(
                            (label) => `
                              <span class="inline-flex bg-white h-5 gap-2.5 items-center justify-center px-2 py-2 alt-body-super-tiny text-black">
                                ${escapeHtml(label)}
                              </span>
                            `
                          )
                          .join('')}
                      </div>
                    `
                    : showListPrice
                    ? `
                      <div class="absolute top-3 left-3 z-10">
                        <span class="inline-flex bg-white h-5 gap-2.5 items-center justify-center px-2 py-2 alt-body-super-tiny text-black">
                          Sale
                        </span>
                      </div>
                    `
                    : ''
                }
              </a>
            </div>
            <a href="${escapeAttr(url)}" class="body-regular text-muted-black">
              ${escapeHtml(name)}
            </a>
            ${
              price
                ? `
                  <p class="body-regular text-muted-black">
                    ${showListPrice ? `<span class="mr-1 text-gray-600 line-through">${escapeHtml(listPrice)}</span>` : ''}
                    <span>${escapeHtml(price)}</span>
                  </p>
                `
                : ''
            }
            </div>
      `;
    }

    async #hydrateProductCards() {
      if (!(this.elements.products instanceof HTMLElement)) return;

      const cardShells = Array.from(this.elements.products.querySelectorAll('[data-nosto-card-shell]')).filter(
        (shell) => shell instanceof HTMLElement && shell.dataset.nostoCardHydrated !== 'true'
      );

      if (!cardShells.length) return;

      await Promise.all(
        cardShells.map(async (shell) => {
          if (!(shell instanceof HTMLElement)) return;

          const cardUrl = shell.dataset.nostoCardUrl;
          if (!cardUrl) return;

          const cacheKey = this.#cardCacheKey(shell.dataset.nostoCardHandle || '', cardUrl);

          try {
            let markup = this.productCardMarkupCache.get(cacheKey);

            if (!markup) {
              const requestUrl = new URL(cardUrl, window.location.origin);
              if (this.collectionHandle && shell.dataset.nostoCardHandle) {
                const storefrontRoot = getStorefrontRoot().replace(/\/$/, '');
                requestUrl.pathname = `${storefrontRoot}/collections/${encodeURIComponent(this.collectionHandle)}/products/${encodeURIComponent(
                  shell.dataset.nostoCardHandle
                )}`;
              }
              requestUrl.searchParams.set('section_id', 'product-card');

              const response = await fetch(requestUrl.toString());
              if (!response.ok) {
                throw new Error(`Failed to load product card section: ${response.status}`);
              }

              markup = await response.text();
              this.productCardMarkupCache.set(cacheKey, markup);
            }

            shell.innerHTML = markup;
            shell.dataset.nostoCardHydrated = 'true';

            if (window.Alpine && typeof Alpine.initTree === 'function') {
              Alpine.initTree(shell);
            }
          } catch (error) {
            console.warn('Unable to hydrate Nosto product card.', error);
          }
        })
      );
    }

    #renderPagination({ total }) {
      if (!(this.elements.pagination instanceof HTMLElement)) return;

      const totalPages = Math.max(1, Math.ceil(total / this.pageSize));
      const currentPage = this.currentPage;

      if (totalPages <= 1) {
        this.elements.pagination.innerHTML = '';
        return;
      }

      if (this.pageMode === 'load-more') {
        const shownCount = Math.min(currentPage * this.pageSize, total);
        this.elements.pagination.innerHTML = `
          <div class="text-center text-sm">
            ${
              currentPage < totalPages
                ? `<button type="button" class="btn-primary inline-block mb-8 w-full md:max-w-md" data-nosto-load-more>Load More</button>`
                : ''
            }
            <div class="text-sm text-gray-600">${escapeHtml(shownCount)} / ${escapeHtml(formatTotal(total))} results</div>
          </div>
        `;
        return;
      }

      this.elements.pagination.innerHTML = `
        <div class="flex items-center justify-center gap-4 text-sm">
          ${
            currentPage > 1
              ? `<button type="button" class="underline" data-nosto-page="${currentPage - 1}">Previous</button>`
              : '<span class="text-gray-400">Previous</span>'
          }
          <span>Page ${escapeHtml(currentPage)} of ${escapeHtml(totalPages)}</span>
          ${
            currentPage < totalPages
              ? `<button type="button" class="underline" data-nosto-page="${currentPage + 1}">Next</button>`
              : '<span class="text-gray-400">Next</span>'
          }
        </div>
      `;
    }

    #setStatus(message) {
      if (!(this.elements.status instanceof HTMLElement)) return;

      if (!message) {
        this.elements.status.classList.add('hidden');
        this.elements.status.textContent = '';
        return;
      }

      this.elements.status.classList.remove('hidden');
      this.elements.status.textContent = message;
    }

    /**
     * Runs work in the frame *after* the next paint.
     *
     * The drawer triggers open the dialog and then want to expand and scroll an accordion into
     * view. `scrollIntoView` and `focus` both force layout, and a single `requestAnimationFrame`
     * put that forced layout in the same frame as `showModal()` — inside the interaction, where
     * it counts towards INP. A second frame lets the drawer paint first.
     *
     * @param {() => void} callback - Work to run once the drawer has painted.
     */
    #afterNextPaint(callback) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
    }

    #updateUrl(mutator) {
      const url = new URL(window.location.href);
      mutator(url);
      window.history.pushState({}, '', url.toString());
      this.currentPage = this.#getCurrentPage();
    }

    async onPopState() {
      this.currentPage = this.#getCurrentPage();
      this.#renderSortOptions();
      try {
        await this.fetchAndRender();
      } catch (error) {
        console.warn('Nosto listing unavailable after navigation.', error);
      }
    }

    async onRootClick(event) {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;

      const showResultsBtn = target.closest('[data-nosto-show-results]');
      if (showResultsBtn instanceof HTMLElement) {
        const host = this.root.querySelector('[data-nosto-plp-drawer]');
        if (host && typeof host.closeDialog === 'function') {
          await host.closeDialog();
        }
        return;
      }

      const sortTrigger = target.closest('[data-nosto-sort-trigger]');
      if (sortTrigger instanceof HTMLElement) {
        const host = this.root.querySelector('[data-nosto-plp-drawer]');
        if (host && typeof host.showDialog === 'function') {
          host.showDialog();
        }
        this.#afterNextPaint(() => {
          const sortAccordion = this.elements.facets?.querySelector('[data-nosto-sort-accordion]');
          if (sortAccordion instanceof HTMLDetailsElement) {
            sortAccordion.open = true;
            sortAccordion.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
          const selectedRadio = this.elements.facets?.querySelector('[data-nosto-sort-radio]:checked');
          if (selectedRadio instanceof HTMLElement) {
            selectedRadio.focus();
          }
        });
        return;
      }

      const shortcutBtn = target.closest('[data-nosto-filter-shortcut]');
      if (shortcutBtn instanceof HTMLElement) {
        const host = this.root.querySelector('[data-nosto-plp-drawer]');
        if (host && typeof host.showDialog === 'function') {
          host.showDialog();
        }
        const field = shortcutBtn.dataset.nostoFacetField;
        if (field && this.elements.facets instanceof HTMLElement) {
          this.#afterNextPaint(() => {
            const details = [...this.elements.facets.querySelectorAll('details[data-nosto-facet-field]')].find(
              (el) => el instanceof HTMLDetailsElement && el.dataset.nostoFacetField === field
            );
            if (details) {
              details.open = true;
              details.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          });
        }
        return;
      }

      const removeFilterButton = target.closest('[data-nosto-remove-filter]');
      if (removeFilterButton instanceof HTMLElement) {
        const field = removeFilterButton.dataset.field;
        const displayValue = removeFilterButton.dataset.displayValue;
        const value = removeFilterButton.dataset.value;
        if (!field || (!displayValue && !value)) return;

        this.#updateUrl((url) => {
          if (displayValue) {
            const nextDisplayValues = url.searchParams.getAll(`nfd.${field}`).filter((item) => item !== displayValue);
            this.#writeDisplayFacetSelection(url, field, nextDisplayValues);
          } else {
            const remaining = url.searchParams.getAll(`nf.${field}`).filter((item) => item !== value);
            url.searchParams.delete(`nf.${field}`);
            remaining.forEach((item) => url.searchParams.append(`nf.${field}`, item));
          }
          url.searchParams.delete('page');
        });

        await this.fetchAndRender();
        return;
      }

      const clearRangeButton = target.closest('[data-nosto-clear-range]');
      if (clearRangeButton instanceof HTMLElement) {
        const field = clearRangeButton.dataset.field;
        if (!field) return;

        this.#updateUrl((url) => {
          url.searchParams.delete(`nfr.${field}.gt`);
          url.searchParams.delete(`nfr.${field}.gte`);
          url.searchParams.delete(`nfr.${field}.lt`);
          url.searchParams.delete(`nfr.${field}.lte`);
          url.searchParams.delete('page');
        });

        await this.fetchAndRender();
        return;
      }

      const clearAllButton = target.closest('[data-nosto-clear-all]');
      if (clearAllButton instanceof HTMLElement) {
        this.#updateUrl((url) => {
          Array.from(url.searchParams.keys()).forEach((key) => {
            if (key.startsWith('nf.') || key.startsWith('nfd.') || key.startsWith('nfr.') || key === 'page') {
              url.searchParams.delete(key);
            }
          });
        });

        await this.fetchAndRender();
        return;
      }

      const pageButton = target.closest('[data-nosto-page]');
      if (pageButton instanceof HTMLElement) {
        const page = Number(pageButton.dataset.nostoPage);
        if (!page) return;

        this.#updateUrl((url) => {
          url.searchParams.set('page', String(page));
        });

        await this.fetchAndRender();
        return;
      }

      const loadMoreButton = target.closest('[data-nosto-load-more]');
      if (loadMoreButton instanceof HTMLElement) {
        this.currentPage += 1;
        this.#updateUrl((url) => {
          url.searchParams.set('page', String(this.currentPage));
        });

        await this.fetchAndRender({ append: true });
        return;
      }

      const hit = target.closest('[data-nosto-hit]');
      if (hit instanceof HTMLElement && this.api) {
        const productId = hit.dataset.productId;
        const url = hit.dataset.productUrl;
        if (productId && url && typeof this.api.recordSearchClick === 'function') {
          this.api.recordSearchClick(this.type === 'category' ? 'category' : 'serp', {
            productId,
            url,
          });
        }
      }
    }

    async onRootChange(event) {
      const target = event.target;

      if (target instanceof HTMLInputElement && target.matches('[data-nosto-sort-radio]')) {
        this.#updateUrl((url) => {
          if (target.value === DEFAULT_SORT) {
            url.searchParams.delete('sort_by');
          } else {
            url.searchParams.set('sort_by', target.value);
          }
          url.searchParams.delete('page');
        });

        await this.fetchAndRender();
        return;
      }

      if (target instanceof HTMLInputElement && target.hasAttribute('data-nosto-facet-input')) {
        const field = target.dataset.field;
        if (!field) return;

        this.#updateUrl((url) => {
          const displayKey = `nfd.${field}`;
          const nextDisplayValues = url.searchParams.getAll(displayKey).filter((value) => value !== target.value);
          if (target.checked) nextDisplayValues.push(target.value);
          this.#writeDisplayFacetSelection(url, field, nextDisplayValues);
          url.searchParams.delete('page');
        });

        await this.fetchAndRender();
        return;
      }

    }

    onRootInput(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('[data-nosto-range-min], [data-nosto-range-max]')) return;

      const wrap = target.closest('[data-nosto-range-slider]');
      if (!(wrap instanceof HTMLElement)) return;

      const rangeMin = Number(wrap.dataset.rangeMin);
      const rangeMax = Number(wrap.dataset.rangeMax);
      const minEl = wrap.querySelector('[data-nosto-range-min]');
      const maxEl = wrap.querySelector('[data-nosto-range-max]');
      if (!(minEl instanceof HTMLInputElement) || !(maxEl instanceof HTMLInputElement)) return;

      let low = Number(minEl.value);
      let high = Number(maxEl.value);
      if (low > high) {
        if (target === minEl) minEl.value = String(high);
        else maxEl.value = String(low);
        low = Number(minEl.value);
        high = Number(maxEl.value);
      }

      this.#updateRangeFill(wrap);
      this.#updateRangeLabels(wrap, low, high);
      this.#updateRangeThumbStyles(wrap, low, high);

      window.clearTimeout(this.rangeCommitTimer);
      this.rangeCommitTimer = window.setTimeout(() => {
        void this.#commitRangeFilter(wrap, low, high, rangeMin, rangeMax);
      }, 260);
    }

    async onRootSubmit(event) {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      if (form.matches('[data-nosto-search-form]')) {
        const input = form.querySelector('[data-nosto-search-input]');
        if (
          input instanceof HTMLInputElement &&
          input.value.trim() &&
          this.api &&
          typeof this.api.recordSearchSubmit === 'function'
        ) {
          this.api.recordSearchSubmit(input.value.trim());
        }
        return;
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-nosto-listing-root]').forEach((root) => {
      const controller = new NostoListingController(root);
      controller.init();
    });
  });
})();
